import type { ReactNode } from "react";

/* ------------------------------------------------------------------ */
/*  Marque                                                             */
/* ------------------------------------------------------------------ */

/**
 * La marque en tête de la latérale : le monogramme du cabinet au filet de
 * laiton, le nom de l'application en Garamond, le dossier courant dessous.
 *
 * Elle est la même dans les trois outils : c'est elle qui dit « vous êtes
 * chez le cabinet », avant même que l'écran ne dise lequel.
 */
export function Marque({
  nom,
  detail,
  monogramme = "CE",
}: {
  nom: string;
  detail?: ReactNode;
  monogramme?: string;
}) {
  return (
    <div className="marque">
      <div className="monogramme" aria-hidden="true">
        {monogramme}
      </div>
      <div>
        <strong>{nom}</strong>
        {detail != null && <span>{detail}</span>}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Étiquettes                                                         */
/* ------------------------------------------------------------------ */

export type VarianteEtiquette = "bloquant" | "avertissement" | "info" | "succes" | "neutre";

export function Etiquette({
  variante,
  children,
}: {
  variante: VarianteEtiquette;
  children: ReactNode;
}) {
  return <span className={`etiquette ${variante}`}>{children}</span>;
}

/* ------------------------------------------------------------------ */
/*  Blocs                                                              */
/* ------------------------------------------------------------------ */

export function Indicateur({
  libelle,
  valeur,
  detail,
}: {
  libelle: string;
  valeur: ReactNode;
  detail?: ReactNode;
}) {
  return (
    <div className="indicateur">
      <div className="libelle">{libelle}</div>
      <div className="valeur">{valeur}</div>
      {detail != null && <div className="detail">{detail}</div>}
    </div>
  );
}

export type TypeMessage = "erreur" | "succes" | "avertissement" | "information";

export function Message({
  type,
  titre,
  children,
}: {
  type: TypeMessage;
  titre?: string;
  children?: ReactNode;
}) {
  return (
    <div className={`message ${type}`}>
      {titre != null && <strong>{titre}</strong>}
      {children}
    </div>
  );
}

export function Chargement({ quoi = "les données" }: { quoi?: string }) {
  return <div className="chargement">Chargement de {quoi}…</div>;
}

export function Vide({ children }: { children: ReactNode }) {
  return <div className="vide">{children}</div>;
}

/**
 * En-tête d'une page : un sur-titre en capitales de laiton, le titre en
 * Garamond, une description, et les actions à droite — la principale en
 * bleu nuit plein, une seule par écran.
 */
export function EnTetePage({
  surTitre,
  titre,
  description,
  actions,
}: {
  surTitre?: string;
  titre: string;
  description?: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <div className="entete-page">
      <div>
        {surTitre != null && <div className="sur-titre">{surTitre}</div>}
        <h1>{titre}</h1>
        {description != null && <p>{description}</p>}
      </div>
      {actions != null && <div className="barre-actions">{actions}</div>}
    </div>
  );
}
